import React, { useState, useEffect } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faExclamationTriangle,
    faTimesCircle,
    faClock,
    faShoppingCart,
    faBoxes,
    faCalendarAlt,
    faUser,
    faStore,
    faFilter,
    faSearch,
    faRefresh,
    faChevronLeft,
    faChevronRight,
    faEye,
    faSpinner,
    faFileExport,
    faInfoCircle,
    faTag,
    faPalette,
    faHashtag,
    faBuilding,
    faRulerCombined,
    faBalanceScale,
    faDollarSign
} from '@fortawesome/free-solid-svg-icons';

const ApprovedProductsList = () => {
    const { user, currentShop } = useAuth();
    const [approvedProducts, setApprovedProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        order_number: '',
        product_name: '',
        product_colorcode: '',
        batch_no: '',
        merge_action: '',
        start_date: '',
        end_date: ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [summary, setSummary] = useState(null);
    const itemsPerPage = 20;

    useEffect(() => {
        if (user && currentShop) {
            fetchApprovedProducts();
            fetchSummary();
        }
    }, [user, currentShop, currentPage]);

    const fetchApprovedProducts = async () => {
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/approved_products.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_approved_products',
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    filters: filters,
                    page: currentPage,
                    limit: itemsPerPage
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setApprovedProducts(data.data || []);
                setTotalItems(data.total || 0);
            }
        } catch (error) {
            console.error('Error fetching approved products:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSummary = async () => {
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/approved_products.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_approval_summary',
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setSummary(data.data);
            }
        } catch (error) {
            console.error('Error fetching summary:', error);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1);
    };

    const applyFilters = () => {
        fetchApprovedProducts();
    };

    const resetFilters = () => {
        setFilters({
            order_number: '',
            product_name: '',
            product_colorcode: '',
            batch_no: '',
            merge_action: '',
            start_date: '',
            end_date: ''
        });
        setCurrentPage(1);
        fetchApprovedProducts();
    };

    const getMergeActionBadge = (action) => {
        switch (action) {
            case 'created':
                return { bg: '#10b98120', color: '#10b981', icon: faCheckCircle, text: 'Created New' };
            case 'updated':
                return { bg: '#3b82f620', color: '#3b82f6', icon: faBalanceScale, text: 'Stock Added' };
            case 'failed':
                return { bg: '#ef444420', color: '#ef4444', icon: faTimesCircle, text: 'Failed' };
            default:
                return { bg: '#6b728020', color: '#6b7280', icon: faInfoCircle, text: 'Unknown' };
        }
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    return (
        <div className="customer-list-modern">
            {/* Header */}
            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <div className="header-text">
                        <h1>Approved Products History</h1>
                        <p>Shop: {currentShop?.shop_name} ({currentShop?.shop_code})</p>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            {summary && (
                <div className="summary-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                    <div className="summary-card" style={{ background: '#10b98110', padding: '15px', borderRadius: '8px' }}>
                        <h3 style={{ fontSize: '14px', color: '#6b7280' }}>Total Approved</h3>
                        <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>{summary.totals?.total_items || 0}</p>
                        <p>Items: {summary.totals?.total_quantity || 0} units</p>
                    </div>
                    
                    {summary.by_action?.map(action => {
                        const badge = getMergeActionBadge(action.merge_action);
                        return (
                            <div key={action.merge_action} className="summary-card" style={{ background: `${badge.color}10`, padding: '15px', borderRadius: '8px' }}>
                                <h3 style={{ fontSize: '14px', color: '#6b7280' }}>{badge.text}</h3>
                                <p style={{ fontSize: '24px', fontWeight: 'bold', color: badge.color }}>{action.count}</p>
                                <p>{action.quantity} units</p>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Filters */}
            <div className="filters-row" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <input
                    type="text"
                    placeholder="Order Number"
                    value={filters.order_number}
                    onChange={(e) => handleFilterChange('order_number', e.target.value)}
                    style={{ padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                />
                <input
                    type="text"
                    placeholder="Product Name"
                    value={filters.product_name}
                    onChange={(e) => handleFilterChange('product_name', e.target.value)}
                    style={{ padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                />
                <input
                    type="text"
                    placeholder="Color Code"
                    value={filters.product_colorcode}
                    onChange={(e) => handleFilterChange('product_colorcode', e.target.value)}
                    style={{ padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                />
                <select
                    value={filters.merge_action}
                    onChange={(e) => handleFilterChange('merge_action', e.target.value)}
                    style={{ padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                >
                    <option value="">All Actions</option>
                    <option value="created">Created New</option>
                    <option value="updated">Stock Added</option>
                    <option value="failed">Failed</option>
                </select>
                <button onClick={applyFilters} style={{ padding: '8px 15px', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faFilter} /> Apply
                </button>
                <button onClick={resetFilters} style={{ padding: '8px 15px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faRefresh} /> Reset
                </button>
            </div>

            {/* Table */}
            <div className="table-scroll-container">
                <div className="table-container-modern">
                    <table className="customer-table-modern">
                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>Date Approved</th>
                                <th>Order #</th>
                                <th>Product</th>
                                <th>Color</th>
                                <th>Batch</th>
                                <th>Quantity</th>
                                <th>Total Yards</th>
                                <th>Action</th>
                                <th>Approved By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="10" style={{ textAlign: 'center', padding: '40px' }}>
                                        <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                                    </td>
                                </tr>
                            ) : approvedProducts.length === 0 ? (
                                <tr>
                                    <td colSpan="10" style={{ textAlign: 'center', padding: '40px' }}>
                                        No approved products found
                                    </td>
                                </tr>
                            ) : (
                                approvedProducts.map((item, index) => {
                                    const badge = getMergeActionBadge(item.merge_action);
                                    return (
                                        <tr key={item.id}>
                                            <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                            <td>{new Date(item.approved_at).toLocaleDateString()}</td>
                                            <td>{item.order_number || '-'}</td>
                                            <td>{item.product_name}</td>
                                            <td>{item.product_colorcode || '-'}</td>
                                            <td>
                                                <span style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>
                                                    {item.batch_no}
                                                </span>
                                                {item.existing_batch_no && item.existing_batch_no !== item.batch_no && (
                                                    <span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>
                                                        (merged with {item.existing_batch_no})
                                                    </span>
                                                )}
                                            </td>
                                            <td>{item.quantity}</td>
                                            <td>{item.total_yards}</td>
                                            <td>
                                                <span style={{ background: badge.bg, color: badge.color, padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
                                                    <FontAwesomeIcon icon={badge.icon} size="xs" /> {badge.text}
                                                </span>
                                            </td>
                                            <td>{item.approved_by_username}</td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="pagination-modern">
                    <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} /> Previous
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                    >
                        Next <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default ApprovedProductsList;